import {
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  InputSignal,
  OnDestroy,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormTracking } from '../../../pages/cms/form-tracking/form-tracking.component';
import {
  DATE_FORMAT_LONG,
  ETextType,
  getTemplateText,
  ILanguage,
  IMessageText,
  INormalText,
  isTemplateText,
  ITextLanguageChangedValue,
  ITextLanguageValue,
} from '@web/dtos';
import { IShoppingCartForm } from '../../../entities/shopping-cart-entity';
import { ImportExportGateway } from '../../../services/api/gateways';
import { TextGateway } from '../../../services/api/text/text.gateway';
import { ImportExportService } from '../../../services/import-export/import-export.service';
import { TableCellComponent } from '../table/table-cell/table-cell.component';
import { ToEntityLinkPipe } from '../../../pipes/to-entity-link/to-entity-link-pipe';
import { TextInputComponent } from '../../data-input/form-inputs/text-input/text-input.component';
import { TableHeaderComponent } from '../table/table-header/table-header.component';
import { ToEntityNamePipe } from '../../../pipes/to-entity-name/to-entity-name';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import {
  ESortOrder,
  ITableSortParams,
  TableComponent,
  TableSortOrder,
  toSortOrder,
} from '../table/table.component';
import { SectionComponent } from '../../layout/section/section.component';
import { SectionHeadlineComponent } from '../../layout/section/section-header/section-headline/section-headline.component';
import { ButtonComponent } from '../../feedback/button/button.component';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { LanguageStore } from '../../../services/api/language/language-store.service';
import { CorporateDesignStore } from '../../../services/api/corporate-design/corporate-design-store.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { Subscription } from 'rxjs';
import { ResetChangesDialogComponent } from '../../dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { SectionHeaderComponent } from '../../layout/section/section-header/section-header.component';
import { SectionHeaderActionsComponent } from '../../layout/section/section-header/section-header-actions/section-header-actions.component';
import { LoadingAnimationComponent } from '../../feedback/loading-animation/loading-animation.component';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { AppTextStore } from '../../../services/api/text/app-text-store.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { ToggleChipComponent } from '../toggle-chip/toggle-chip.component';

interface ITextFilterForm {
  search: FormControl<string | null>;
}

@Component({
  selector: 'aw-texts-table',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    SectionHeaderActionsComponent,
    ButtonComponent,
    LoadingAnimationComponent,
    SectionComponent,
    TableComponent,
    AppendTestIdPipe,
    TableHeaderComponent,
    TextInputComponent,
    ToEntityNamePipe,
    ToEntityLinkPipe,
    TableCellComponent,
    ToggleChipComponent,
  ],
  templateUrl: './texts-table.component.html',
  styles: ``,
  providers: [
    ImportExportService,
    {
      provide: ImportExportGateway<INormalText[], FormData, ETextType>,
      useClass: TextGateway,
    },
  ],
})
export class TextsTableComponent
  extends FormTracking<IShoppingCartForm>
  implements OnDestroy
{
  public readonly texts: InputSignal<INormalText[]> = input.required();
  public readonly textType: InputSignal<ETextType> = input.required();
  public readonly isLoading: InputSignal<boolean> = input.required();
  public readonly headline: InputSignal<string> = input.required();

  private readonly dialogService: DialogService = inject(DialogService);
  protected readonly languageStore: LanguageStore = inject(LanguageStore);
  protected readonly corporateDesignStore: CorporateDesignStore =
    inject(CorporateDesignStore);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly importExportService: ImportExportService<
    INormalText[],
    ETextType
  > = inject(ImportExportService<INormalText[], ETextType>);
  private selectedFile: File | null = null;
  private readonly webTextStore: WebTextStore = inject(WebTextStore);
  private readonly appTextStore: AppTextStore = inject(AppTextStore);

  initialValue: string | null = null;
  editingData: any | null = null;
  editingLangPrefix: string | null = null;

  protected readonly DATE_FORMAT_LONG: string = DATE_FORMAT_LONG;

  public readonly filterForm: FormGroup<ITextFilterForm> = new FormGroup({
    search: new FormControl<string | null>(null),
  });
  filename: FormControl = new FormControl();
  public readonly componentSubscriptions: Subscription = new Subscription();
  //this needs to be seeded from languages (hide inactive languages on load)
  protected readonly hiddenColumns: WritableSignal<string[]> = signal<string[]>(
    this.languageStore
      .languages()
      .filter((language: ILanguage) => !language.isActive)
      .map((language: ILanguage) => language.name)
  );
  protected readonly isTemplateText: typeof isTemplateText = isTemplateText;
  protected readonly getTemplateText: typeof getTemplateText = getTemplateText;

  public allTexts: INormalText[] = [];
  public filteredTexts: INormalText[] = [];

  constructor() {
    super();
    effect((): void => {
      this.allTexts = this.texts();
      this.filteredTexts = this.allTexts;
    });
    this.componentSubscriptions.add(
      toObservable(this.languageStore.languages).subscribe(
        (languages: ILanguage[]) => {
          const hiddenLanguages: string[] = languages
            .filter((language: ILanguage) => !language.isActive)
            .map((language: ILanguage) => language.name);
          setTimeout(() => this.hiddenColumns.set(hiddenLanguages), 0);
          this.cdRef.detectChanges();
        }
      )
    );
    this.componentSubscriptions.add(
      this.filterForm.valueChanges.subscribe(value => {
        this.filteredTexts = this.filterTexts(
          this.allTexts,
          this.toTextFilterValue(value as any)
        );
      })
    );
    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (this.filterForm.dirty) {
        event.preventDefault();
      }
    };
  }

  ngOnDestroy(): void {
    this.componentSubscriptions.unsubscribe();
  }

  save(): void {
    this.filterForm.markAsPristine();
    const changedTexts: INormalText[] = [];
    this.allTexts.forEach((normalText: INormalText) => {
      let hasChangedTexts = false;
      normalText.text.values.forEach((value: ITextLanguageChangedValue) => {
        if (value.isChanged) {
          hasChangedTexts = true;
        }
      });
      if (hasChangedTexts) {
        changedTexts.push(normalText);
      }
    });
    if (this.textType.name === ETextType.Web) {
      this.webTextStore.save(changedTexts);
    } else {
      this.appTextStore.save(changedTexts);
    }
  }

  get hasChanges(): boolean {
    return !!this.allTexts.find(text =>
      text.text.values.find(
        (textValueForLanguage: ITextLanguageChangedValue) =>
          textValueForLanguage.isChanged
      )
    );
  }

  startEdit(data: INormalText | IMessageText, language: ILanguage): void {
    this.initialValue = data.text.values.map(v => v.value).find(v => v) || '';
    this.editingData = data;
    this.editingLangPrefix = language.prefix;
  }

  stopEdit(): void {
    this.editingData = null;
    this.editingLangPrefix = null;
    this.cdRef.detectChanges();
  }

  stopEditAndReset(language: ILanguage): void {
    this.onValueChange(this.initialValue || '', this.editingData, language);
    this.stopEdit();
  }

  isEditing(data: any | null, language: ILanguage): boolean {
    return (
      this.editingData?.entityType === data?.entityType &&
      this.editingData?.relationType === data?.relationType &&
      this.editingData?.id === data?.id &&
      this.editingLangPrefix === language.prefix
    );
  }

  onValueChange(newValue: string, data: any, language: ILanguage): void {
    this.filterForm.markAsDirty();
    const languageText: INormalText = data as INormalText;
    this.allTexts = this.allTexts.map((text: INormalText): INormalText => {
      return this.changeValueInTexts(text, languageText, language, newValue);
    });
    this.filteredTexts = this.filteredTexts.map(
      (text: INormalText): INormalText => {
        return this.changeValueInTexts(text, languageText, language, newValue);
      }
    );
  }

  private changeValueInTexts(
    normalText: INormalText,
    languageText: INormalText,
    language: ILanguage,
    newValue: string
  ): INormalText {
    if (normalText.id === languageText.id) {
      let hasChangedTexts = false;
      const newValues: (ITextLanguageValue | ITextLanguageChangedValue)[] =
        normalText.text.values.map(
          (
            val: ITextLanguageValue | ITextLanguageChangedValue
          ): ITextLanguageValue | ITextLanguageChangedValue => {
            if (language.prefix === val.languagePrefix) {
              hasChangedTexts = true;
              return { ...val, value: newValue, isChanged: true };
            }
            return val;
          }
        );
      if (!hasChangedTexts) {
        return {
          ...normalText,
          text: {
            ...normalText.text,
            values: [
              ...newValues,
              {
                languagePrefix: language.prefix,
                value: newValue,
                isChanged: true,
              },
            ],
          },
        };
      }
      return { ...normalText, text: { ...normalText.text, values: newValues } };
    } else {
      return normalText;
    }
  }

  reload(): void {
    const dialogRef = this.dialogService.openDialog<{
      icon: string;
      iconBackgroundColor: string;
    }>(ResetChangesDialogComponent, {
      headline: this.cmsTextProvider.shared.revertChanges,
      width: '600px',
      icon: 'warning',
      iconBackgroundColor: '#eed202',
    });
    dialogRef.closed.subscribe((result: any) => {
      if (result != undefined && result.submit) {
        if (this.textType() === ETextType.Web) {
          this.webTextStore.reload();
        } else {
          this.appTextStore.reload();
        }
      }
    });
  }

  public filterTexts(
    allSubscriptions: INormalText[],
    filter: any
  ): INormalText[] {
    return allSubscriptions.filter((s: INormalText): boolean =>
      this.searchIncludesSubstring(s, filter.search)
    );
  }

  private searchIncludesSubstring(
    subscription: INormalText,
    subStringFilter: string | null | undefined
  ): boolean {
    if (!subStringFilter) {
      return true;
    }

    const filter: string = subStringFilter.toLowerCase();

    return (
      [subscription.entityType, subscription.relationType].some(
        (field: string): boolean => field?.toLowerCase().includes(filter)
      ) ||
      subscription.text.values.some((field): boolean =>
        field?.value.toLowerCase().includes(filter)
      )
    );
  }

  private toTextFilterValue(value: any): any {
    const compensateNull = <T>(value: T) => (value === 'null' ? null : value);

    return {
      search: compensateNull(value.search),
    };
  }

  public textEntityTypeFilter: ITableSortParams<INormalText>['compare'] = (
    a: INormalText,
    b: INormalText,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    return a.entityType.toLowerCase() > b.entityType.toLowerCase()
      ? 1 * sortDirection
      : -1 * sortDirection;
  };

  public textRelationTypeFilter: ITableSortParams<INormalText>['compare'] = (
    a: INormalText,
    b: INormalText,
    order: TableSortOrder
  ): number => {
    const sortDirection: ESortOrder = toSortOrder(order);
    return a.relationType.toLowerCase() > b.relationType.toLowerCase()
      ? 1 * sortDirection
      : -1 * sortDirection;
  };

  public textValueFilter: ITableSortParams<INormalText>['compare'] = (
    a: INormalText,
    b: INormalText,
    order: TableSortOrder,
    lang?: string
  ): number => {
    const sortDirection: ESortOrder = toSortOrder(order);
    const aValue = a.text.values.filter(val => val.languagePrefix === lang)[0];
    const bValue = b.text.values.filter(val => val.languagePrefix === lang)[0];

    if (aValue === undefined) {
      return 1 * sortDirection;
    }
    if (bValue === undefined) {
      return -1 * sortDirection;
    }
    return aValue.value.toLowerCase() > bValue.value.toLowerCase()
      ? 1 * sortDirection
      : -1 * sortDirection;
  };

  public dateLastEditedAtFilter: ITableSortParams<INormalText>['compare'] = (
    a: INormalText,
    b: INormalText,
    order: TableSortOrder
  ): number => {
    const sortDirection = toSortOrder(order);
    const dateA = new Date(a.text.lastEditedAt ?? new Date());
    const dateB = new Date(b.text.lastEditedAt ?? new Date());
    return (dateA.getTime() - dateB.getTime()) * sortDirection;
  };

  isActiveLanguages(language: ILanguage): boolean {
    return !this.hiddenColumns().some(l => l === language.name);
  }

  toggleLanguage(language: ILanguage) {
    if (this.hiddenColumns().includes(language.name)) {
      this.hiddenColumns.set(
        this.hiddenColumns().filter(
          (languageName: string) => languageName !== language.name
        )
      );
    } else {
      this.hiddenColumns.set(this.hiddenColumns().concat([language.name]));
    }
  }

  getLanguageValue(data: any, language: ILanguage): string {
    const normalText: INormalText = data as INormalText;
    return (
      normalText.text.values.filter(
        (l: ITextLanguageChangedValue) => l.languagePrefix === language.prefix
      )[0]?.value ?? ''
    );
  }

  getLanguageIsChanged(data: any, language: ILanguage): boolean {
    const normalText: INormalText = data as INormalText;
    return (
      (normalText.text.values as ITextLanguageChangedValue[]).filter(
        (l: ITextLanguageChangedValue) => l.languagePrefix === language.prefix
      )[0]?.isChanged ?? false
    );
  }

  getChangedCountForLang(language: ILanguage): number {
    let count = 0;
    this.allTexts.forEach((normalText: INormalText) => {
      if (
        normalText.text.values.filter(
          (val: ITextLanguageChangedValue) =>
            val.languagePrefix === language.prefix && val.isChanged
        ).length > 0
      ) {
        count++;
      }
    });
    return count;
  }

  exportTexts(): void {
    this.importExportService.downloadFile(this.textType(), 'shop-text.csv');
  }

  private getFilenameFromHeader(
    contentDisposition: string | null
  ): string | null {
    if (!contentDisposition) return null;

    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
      contentDisposition
    );
    if (!matches || matches.length < 2) return null;

    return matches[1].replace(/['"]/g, '');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    this.selectedFile = input.files[0];

    this.uploadFile();
    input.value = '';
  }

  uploadFile(): void {
    if (!this.selectedFile) return;

    const formData = new FormData();
    formData.append('file', this.selectedFile);

    this.importExportService.uploadFile(formData).subscribe({
      next: res => {
        const texts: INormalText[] = res!.data!;
        const mergedNormalTexts: INormalText[] = [
          ...this.allTexts.filter(item => !texts.some(u => u.id === item.id)),
          ...texts,
        ];

        mergedNormalTexts.forEach((updatedNormalText: INormalText) => {
          const originalText = this.allTexts.find(
            (originalNormalText: INormalText) =>
              originalNormalText.id === updatedNormalText.id
          );
          if (!originalText) return;

          updatedNormalText.text.values.forEach(
            (updatedLanguageValue: ITextLanguageChangedValue) => {
              const originalLanguageValue = originalText.text.values.find(
                originalLanguageValue =>
                  originalLanguageValue.id === updatedLanguageValue.id
              );
              if (!originalLanguageValue) {
                updatedLanguageValue.isChanged = true;
              } else {
                updatedLanguageValue.isChanged =
                  updatedLanguageValue.value !== originalLanguageValue.value;
              }
            }
          );
        });

        this.allTexts = mergedNormalTexts;
        this.filteredTexts = mergedNormalTexts;
      },
      error: err => console.error('Fehler beim Upload', err),
    });
  }
}
